// menu-position — pure positioning logic for portal-mounted row/action menus.
//
// Extracted from LxRowMenu so the flip-on-edge logic is a dependency-free,
// directly-testable pure function (no React / DOM imports). LxRowMenu imports
// computeMenuPos from here and the unit test imports the SAME function — the
// test exercises the shipped code, never a re-implemented mirror.
//
// Vertical: if the menu would overflow the viewport bottom, open ABOVE the
// trigger instead of below. `top` is clamped to >= 8 so the menu never escapes
// the viewport top. PURELY ADDITIVE: when there is room below (the common
// case), behavior is UNCHANGED — the menu opens downward at rect.bottom + 4.
//
// Horizontal (NEW — fixes RTL off-screen clipping): `right` is a physical
// CSS `right` value (the menu uses `position:fixed; right:`). It does NOT
// flip with `dir`, so the clamp below is correct for both LTR and RTL.
// In Arabic/RTL the row trigger sits near the LEFT edge of the viewport, so
// `viewportWidth - rect.right` is large → without the clamp the menu's
// left edge goes negative (off-screen). The clamp keeps the menu fully on
// screen in BOTH directions. Verified for RTL (primary) and LTR:
//   RTL case: trigger near left edge → rect.right small → right large →
//     clamped to maxRight = viewportWidth - menuWidth - 8 → menu stays on screen.
//   LTR case: trigger near right edge → rect.right large → right near 0 →
//     clamped below by `if (right < edge) right = edge` → right edge stays on screen.
//
// estimatedMenuHeight: caller-provided estimate of the rendered menu height
// (LxRowMenu passes itemCount * 36 + 8). The estimate only needs to be close
// enough to choose the open-above vs open-below branch.
//
// menuWidth: caller-provided estimate of the rendered menu width used for the
// horizontal clamp. Defaults to 180. LxRowMenu passes its `minWidth` prop.
//
// D-NUMBERS-EN-01: all numeric literals are Latin digits.

export interface MenuTriggerRect {
  top: number;
  bottom: number;
  right: number;
}

export interface MenuPos {
  top: number;
  right: number;
}

export function computeMenuPos(
  rect: MenuTriggerRect,
  viewportWidth: number,
  viewportHeight: number,
  estimatedMenuHeight: number,
  menuWidth = 180,
  margin = 4,
): MenuPos {
  // ── Vertical ──────────────────────────────────────────────────────────────
  const spaceBelow = viewportHeight - rect.bottom - margin;
  let top: number;
  if (spaceBelow < estimatedMenuHeight) {
    // Flip: open above the trigger.
    top = rect.top - estimatedMenuHeight - margin;
    if (top < 8) top = 8; // clamp: never above the viewport top
  } else {
    top = rect.bottom + margin;
  }

  // ── Horizontal (direction-agnostic physical CSS `right`) ──────────────────
  const edge = 8;
  let right = viewportWidth - rect.right;
  // Prevent the menu's LEFT edge from going off-screen (RTL primary case):
  //   menu left edge = viewportWidth - right - menuWidth; clamp ≥ edge
  //   → right ≤ viewportWidth - menuWidth - edge
  const maxRight = Math.max(edge, viewportWidth - menuWidth - edge);
  if (right > maxRight) right = maxRight;
  // Prevent the menu's RIGHT edge from going off-screen (LTR secondary case):
  //   menu right edge = viewportWidth - right; clamp ≥ edge
  //   → right ≤ viewportWidth - edge (already satisfied above, but also:)
  //   right ≥ edge so right edge ≤ viewportWidth - edge (on-screen)
  if (right < edge) right = edge;

  return { top, right };
}
